﻿import {DateOnly} from "./DateOnly";
import {
	createFromNumbers,
	dateCompare,
	dateEquals,
	fromUtc,
	getLastOfMonth,
	throwsIfInvalidDate
} from "./DateTimeHelpers";
import {DayOfWeek} from "./DayOfWeek";
import {TimeOnly} from "./TimeOnly";

function toISOString(date: Date): string {
	return fromUtc(date).toISOString();
}

export class DateTime {
	private static _minValue: DateTime | null = null;
	private static _maxValue: DateTime | null = null;
	private readonly _date: Date;

	private constructor(date: Date, isUtc: boolean = false) {
		this._date = isUtc ? date : new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
			date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());

		throwsIfInvalidDate(this._date, "DateTime");
	}

	get [Symbol.toStringTag](): string {
		return "DateTime";
	}

	static minValue(): DateTime {
		return this._minValue ??= DateTime.fromTimestamp(0);
	}

	static maxValue(): DateTime {
		return this._maxValue ??= DateTime.fromTimestamp(8640000000000000);
	}

	static fromDate(date: Date): DateTime {
		return new DateTime(date);
	}

	static fromTimestamp(ticks: number): DateTime {
		return new DateTime(new Date(ticks), true);
	}

	static fromIsoString(isoString: string): DateTime {
		return new DateTime(new Date(isoString));
	}

	static fromComponents(year: number, month: number, day: number,
	                      hours: number = 0, minutes: number = 0,
	                      seconds: number = 0, milliseconds: number = 0): DateTime {
		return new DateTime(createFromNumbers(year, month, day, hours, minutes, seconds, milliseconds));
	}

	static fromDateAndTime(date: DateOnly, time: TimeOnly): DateTime {
		return new DateTime(new Date(date.getYear(), date.getMonth() - 1, date.getDay(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds()), true);
	}

	static fromDateOnly(date: DateOnly): DateTime {
		return new DateTime(new Date(date.getYear(), date.getMonth() - 1, date.getDay(), 0, 0, 0, 0), true);
	}

	static utcNow(): DateTime {
		return new DateTime(new Date());
	}

	static localNow(): DateTime {
		return new DateTime(fromUtc(new Date()));
	}

	getYear(): number {
		return this._date.getFullYear();
	}

	/**
	 * Returns an integer, between 1 and 12, representing the month for the given date according to local time.
	 */
	getMonth(): number {
		return this._date.getMonth() + 1;
	}

	/**
	 * Returns an integer, between 1 and 31, representing the day of the month for the given date according to local time.
	 * @return {number} The day of the month as a number.
	 */
	getDay(): number {
		return this._date.getDate();
	}

	getDayOfWeek(): DayOfWeek {
		return this._date.getDay();
	}

	getHours(): number {
		return this._date.getHours();
	}

	getMinutes(): number {
		return this._date.getMinutes();
	}

	getSeconds(): number {
		return this._date.getSeconds();
	}

	getMilliseconds(): number {
		return this._date.getMilliseconds();
	}

	addSeconds(seconds: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setSeconds(newDate.getSeconds() + seconds);
		return new DateTime(newDate, true);
	}

	addMinutes(minutes: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setMinutes(newDate.getMinutes() + minutes);
		return new DateTime(newDate, true);
	}

	addHours(hours: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setHours(newDate.getHours() + hours);
		return new DateTime(newDate, true);
	}

	addDays(days: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setDate(newDate.getDate() + days);
		return new DateTime(newDate, true);
	}

	addWeeks(weeks: number): DateTime {
		return this.addDays(weeks * 7);
	}

	addMonths(months: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setMonth(newDate.getMonth() + months);
		return new DateTime(newDate, true);
	}

	addYears(years: number): DateTime {
		const newDate = new Date(this._date);
		newDate.setFullYear(newDate.getFullYear() + years);
		return new DateTime(newDate, true);
	}

	getUtcDate(): Date {
		return this._date;
	}

	getLastOfMonth(): number {
		return getLastOfMonth(this._date);
	}

	startOfDay(): DateTime {
		return DateTime.fromComponents(this._date.getFullYear(), this._date.getMonth() + 1, this._date.getDate());
	}

	equals(other: DateTime | null | undefined): boolean {
		return dateEquals(this._date, other?._date);
	}

	isSameDate(other: DateTime | null | undefined): boolean {
		if (other == null) {
			return false;
		}
		return this.getYear() === other.getYear() && this.getMonth() === other.getMonth() && this.getDay() === other.getDay();
	}

	compareTo(other: DateTime | null | undefined): number {
		return dateCompare(this._date, other?._date);
	}

	toDateOnly(): DateOnly {
		return DateOnly.fromDate(this._date, true);
	}

	toTimeOnly(): TimeOnly {
		return TimeOnly.fromComponents(this.getHours(), this.getMinutes(), this.getSeconds(), this.getMilliseconds());
	}

	toDateOnlyString(): string {
		return toISOString(this._date).slice(0, 10);
	}

	toTimeOnlyString(): string {
		return toISOString(this._date).slice(11, 19);
	}

	toString(): string {
		return toISOString(this._date);
	}

	[Symbol.toPrimitive](hint: string): number | string {
		//hint: [default|number|string]
		if (hint === "number") {
			return this._date.getTime();
		}

		return toISOString(this._date);
	}

	toJSON(): string {
		return toISOString(this._date);
	}
}
